import React from 'react';
import {Box, Typography} from '@mui/material';
import {useTranslate} from 'react-admin';
import {t} from '../themes/statamicTokens';
import StatusPill from '../components/statamic/StatusPill';
import EmptyState from '../components/statamic/EmptyState';

const cell = {
    px: 2,
    py: 1.25,
    verticalAlign: 'top',
    fontSize: `${t.type.body.size}px`,
    color: t.color.text.primary,
    lineHeight: 1.5,
};

const Empty = () => (
    <Box component="span" sx={{color: t.color.text.muted}}>—</Box>
);

const ChildTranslationTable = ({translations}) => {
    const translate = useTranslate();

    if (!translations || translations.length === 0) {
        return (
            <EmptyState
                title={translate('No translations yet')}
                description={translate('Translations of the child story will appear here once the content has been translated.')}
                dense
            />
        );
    }

    return (
        <Box
            sx={{
                border: `1px solid ${t.color.border.default}`,
                borderRadius: `${t.radius.md}px`,
                overflow: 'hidden',
                backgroundColor: t.color.surface,
            }}
        >
            <Box component="table" sx={{width: '100%', borderCollapse: 'collapse'}}>
                <Box component="thead">
                    <Box component="tr">
                        {[translate('Language'), translate('Intro'), translate('Family'), translate('Help'), translate('Original')].map(label => (
                            <Box
                                key={label}
                                component="th"
                                sx={{
                                    textAlign: 'left',
                                    px: 2,
                                    py: 1,
                                    fontSize: `${t.type.caption.size}px`,
                                    fontWeight: 600,
                                    color: t.color.text.secondary,
                                    borderBottom: `1px solid ${t.color.border.default}`,
                                }}
                            >
                                {label}
                            </Box>
                        ))}
                    </Box>
                </Box>
                <Box component="tbody">
                    {translations.map((tr, i) => (
                        <Box
                            component="tr"
                            key={tr.language_code ?? tr.languageCode ?? tr.id}
                            sx={{
                                '& > td': {
                                    borderBottom: i === translations.length - 1 ? 'none' : `1px solid ${t.color.border.hairline}`,
                                },
                            }}
                        >
                            <Box component="td" sx={cell}>
                                <StatusPill variant="neutral">
                                    {String(tr.language_code ?? tr.languageCode ?? '').toUpperCase()}
                                </StatusPill>
                            </Box>
                            <Box component="td" sx={cell}>
                                <Typography sx={{fontSize: `${t.type.body.size}px`, whiteSpace: 'pre-wrap'}}>
                                    {tr.intro || <Empty/>}
                                </Typography>
                            </Box>
                            <Box component="td" sx={cell}>
                                <Typography sx={{fontSize: `${t.type.body.size}px`, whiteSpace: 'pre-wrap'}}>
                                    {tr.family || <Empty/>}
                                </Typography>
                            </Box>
                            <Box component="td" sx={cell}>
                                <Typography sx={{fontSize: `${t.type.body.size}px`, whiteSpace: 'pre-wrap'}}>
                                    {tr.help || <Empty/>}
                                </Typography>
                            </Box>
                            <Box component="td" sx={{...cell, width: 110}}>
                                {tr.original ? (
                                    <StatusPill variant="success">{translate('Original')}</StatusPill>
                                ) : (
                                    <Box component="span" sx={{color: t.color.text.muted, fontSize: `${t.type.caption.size}px`}}>—</Box>
                                )}
                            </Box>
                        </Box>
                    ))}
                </Box>
            </Box>
        </Box>
    );
};

export default ChildTranslationTable;
